import { serverMessage } from '../error/server-message'
import { formatDate, plural } from '../sso-workspace/format'
import { BOUND_EXPIRY } from '../../layout/authorized/org/api-keys/key-words'

// The words and rules of SCIM provisioning in the admin (spec
// 2026-10-23-scim-provisioning-design R3, R6, R22), shared by the staff
// connection workspace and the organization's Single sign-on page. The
// server is the authority: these only shape what is offered and say what it
// answered.

export const SCIM_TITLE = 'Provisioning (SCIM)'

// R3: a public sign-in option never gets provisioning; content's link row
// says so with reason 'public', and the same sentence is its refusal.
export const PUBLIC_NOTE = 'Not available for a public sign-in option'

// R3: Google Workspace (issuer accounts.google.com, content's
// connection.google) provisions only to apps in Google's own catalog. The
// server does not branch on it: this is a note, not a refusal.
export const GOOGLE_NOTE = "Google only provisions to apps in its own catalog, so SCIM isn't available from Google yet."

// R7: SCIM_PUBLIC_BASE_URL is unset on this stack (reason 'not_configured').
export const NOT_CONFIGURED_NOTE = "Provisioning (SCIM) isn't configured here."

// R3: turning provisioning on and rotating need the SSO plan; revoking and
// what the identity provider removes never do.
export const PLAN_OFF_NOTE = "Provisioning (SCIM) is part of the SSO plan. Turning it on and rotating the token need the plan; revoking the token doesn't."

// R22: what the organization's section says about who is in charge.
export const ORG_INTRO = 'People provisioned by your identity provider are added and removed there.'

// R13: an admin's removal of a provisioned person is undone by the identity
// provider's next write for them.
export const REMOVE_IN_IDP = "To remove someone your identity provider provisioned, remove them there: if you remove them here, the identity provider adds them back the next time it updates them."

export const READ_ONLY_IMPERSONATING = "You're signed in as someone else, so provisioning can be viewed but not changed. End the impersonation to make changes."

// R6 (the LMS rules): 365 days by default, Never offered with this warning,
// at most 3 years.
export const TOKEN_EXPIRY = BOUND_EXPIRY
export const NEVER_TOKEN_WARNING = 'A token that never expires works until someone revokes it. Prefer an expiry date, and rotate the token before that date.'

// How the identity provider authenticates (spec R26), per link: the Bearer
// token itself, or OAuth 2.0 client credentials (a client ID and secret it
// exchanges at the token endpoint for an hour's access token; Microsoft
// Entra's "OAuth2 client credentials grant"). content's link row says which
// (authMethod), and whether OAuth can be chosen here (oauthAvailable).
export const METHOD_BEARER = 'bearer'
export const METHOD_OAUTH = 'oauth'
export const METHOD_LABELS = {
  [METHOD_BEARER]: 'Bearer token',
  [METHOD_OAUTH]: 'OAuth client credentials'
}
export const METHOD_HINTS = {
  [METHOD_BEARER]: 'One long-lived secret token your identity provider sends with every request.',
  [METHOD_OAUTH]: 'A client ID and secret your identity provider exchanges for a token that lasts an hour. Microsoft Entra calls it "OAuth2 client credentials grant".'
}

// An OAuth client secret expires in one to three years (the rule Microsoft's
// app gallery sets for provisioning apps; content enforces it): 1, 2 or 3
// years, a year by default, no Never and no shorter or custom date. The
// Bearer token keeps TOKEN_EXPIRY.
export const OAUTH_SECRET_EXPIRY = {
  choices: [
    { value: '365', label: '1 year' },
    { value: '730', label: '2 years' },
    { value: '1095', label: '3 years' }
  ],
  defaultChoice: '365',
  maxYears: 3
}
export const OAUTH_EXPIRY_NOTE = 'An OAuth client secret expires in 1 to 3 years, as Microsoft requires.'

// The expiry rules of a credential of this method.
export const expiryRules = method => (method === METHOD_OAUTH ? OAUTH_SECRET_EXPIRY : TOKEN_EXPIRY)

// The link's method while it has a token: content's authMethod, else the
// Bearer token (a server without OAuth); null without a token.
export const linkMethod = link => {
  if (!link || !link.token) return null
  return link.authMethod === METHOD_OAUTH ? METHOD_OAUTH : METHOD_BEARER
}

// The method in words, for a row ("" without a token).
export const methodText = link => {
  const m = linkMethod(link)
  return m ? METHOD_LABELS[m] : ''
}

// What the credential is called on a link: OAuth's client secret, or the
// token.
export const credentialNoun = link => (linkMethod(link) === METHOD_OAUTH ? 'client secret' : 'token')

// A rotate's expiry (content's rotate takes an optional expires_at): keep
// the current date (the default; nothing is sent) or one of TOKEN_EXPIRY's.
export const KEEP_EXPIRY = 'keep'

// Whether two expiries are the same (both never, or the same instant).
export const sameExpiry = (a, b) => {
  if (!a && !b) return true
  if (!a || !b) return false
  return Date.parse(a) === Date.parse(b)
}

// A link's provisioning state, in words: { key, label, tone, note }.
//   unavailable: a public connection (never), or SCIM not configured here
//   on / expiring / expired: the newest unrevoked token's status
//   off: no token
export const linkState = link => {
  if (!link) return { key: 'off', label: 'Off', tone: null }
  const connection = link.connection || {}
  if (connection.public || link.reason === 'public') return { key: 'public', label: 'Not available', tone: null, note: PUBLIC_NOTE }
  const token = link.token
  if (token && token.status === 'expired') return { key: 'expired', label: 'Token expired', tone: 'bad' }
  if (token && token.status === 'expiring') return { key: 'expiring', label: 'On · expires soon', tone: 'warn' }
  if (token) return { key: 'on', label: 'On', tone: 'ok' }
  if (link.reason === 'not_configured' || link.configured === false) return { key: 'not_configured', label: 'Not configured', tone: null, note: NOT_CONFIGURED_NOTE }
  return { key: 'off', label: 'Off', tone: null }
}

// What a person may do on a link ({ canEdit, impersonating, staff,
// planOff }):
// turn on (no live token: none, or an expired one, which turning on
// replaces), rotate (the organization's own live token; never staff) and
// revoke (any unrevoked token). The server refuses what the plan or the
// configuration doesn't allow; these only keep the page from offering it.
// planOff: the organization's SSO plan is off (the page knows it), so only
// revoking is offered, whatever the row says (R3).
export const linkActions = (link, { canEdit, impersonating, staff, planOff } = {}) => {
  const state = linkState(link)
  const writer = Boolean(canEdit) && !impersonating
  const live = state.key === 'on' || state.key === 'expiring'
  const unavailable = state.key === 'public' || state.key === 'not_configured'
  const planOn = !planOff && (!link || link.planOn !== false)
  return {
    turnOn: writer && !live && !unavailable && planOn,
    rotate: writer && live && !staff && planOn,
    // Switch between the Bearer token and OAuth (R26): like a rotation, the
    // organization's own, on a live token, where OAuth is available.
    switchMethod: writer && live && !staff && planOn && Boolean(link && link.oauthAvailable),
    revoke: writer && Boolean(link && link.token),
    planBlocked: writer && !live && !unavailable && !planOn
  }
}

// "Oct 23, 2027", or "Never" for a token with no expiry.
export const expiryText = token => (token ? (token.expiresAt ? formatDate(token.expiresAt) : 'Never') : '')

// The token's prefix as shown: the first characters, then an ellipsis.
export const tokenPrefixText = token => (token && token.prefix ? `${token.prefix}…` : '')

// Who made the token: a person the organization reads by name, else
// EducationXR support (content never names staff to an organization).
export const madeByText = token => {
  if (!token) return ''
  if (token.createdBySupport) return 'EducationXR support'
  return token.createdBy || ''
}

// The base URL of a connection: every link of it has the same one (R2: the
// token, not the URL, names the organization).
export const baseURLOf = links => {
  const found = (Array.isArray(links) ? links : []).find(l => l && l.baseURL)
  return found ? found.baseURL : null
}

// A refused write in words: content's sentences are written for people, so
// they are shown as they are, except where core's own sentence is about
// another kind of key.
const CODE_TEXT = {
  'api_key.binding_taken': 'Provisioning is already on for this connection. Rotate the token, or revoke it and turn provisioning on again.',
  'api_key.invalid_expiry': 'Choose an expiry after today and at most 3 years away.',
  'api_key.invalid_grace': 'Choose how long the old token keeps working: up to 7 days.',
  'scim.same_auth_method': 'Provisioning already uses this authentication method.',
  'auth.impersonation_not_allowed': READ_ONLY_IMPERSONATING
}

export const scimErrorText = (err, fallback) => {
  const code = err && err.data && err.data.code
  if (code && CODE_TEXT[code]) return CODE_TEXT[code]
  return serverMessage(err, fallback)
}

// An activity event in words (content's /activity: scim.* events of the
// link and its tokens' api_key.* events; ids and counts only, never a name
// or an address).
const sessionsText = data => {
  const n = Number(data && data.sessions_ended)
  return Number.isFinite(n) && n > 0 ? ` and ended ${plural(n, 'session')}` : ''
}

const FIELD_WORDS = {
  given_name: 'first name',
  givenName: 'first name',
  family_name: 'last name',
  familyName: 'last name',
  display_name: 'display name',
  displayName: 'display name',
  external_id: 'external id',
  externalId: 'external id',
  user_name: 'user name',
  userName: 'user name',
  email: 'email address',
  active: 'active'
}

// fields: a list, or content's comma-joined text ("displayName, externalId").
const fieldsOf = data => {
  const fields = data && data.fields
  if (Array.isArray(fields)) return fields
  if (typeof fields === 'string') return fields.split(',').map(f => f.trim()).filter(Boolean)
  return []
}

const fieldsText = data => {
  const fields = fieldsOf(data)
  return fields.length ? ` (${fields.map(f => FIELD_WORDS[f] || String(f).replace(/_/g, ' ')).join(', ')})` : ''
}

const byText = data => (data && data.by_staff ? ' by EducationXR support' : data && data.by_system ? ' by EducationXR' : '')

// A rotation, a client secret's rotation (OAuth), or a switch of method.
const rotatedText = data => {
  const old = (data.previous_auth_method || data.auth_method) === METHOD_OAUTH ? 'client secret' : 'token'
  const grace = data.grace_hours ? `; the old ${old} worked ${plural(data.grace_hours, 'hour')} more` : ''
  if (data.previous_auth_method && data.auth_method && data.previous_auth_method !== data.auth_method) {
    return `Switched to ${data.auth_method === METHOD_OAUTH ? 'OAuth client credentials' : 'the Bearer token'}${byText(data)}${grace}`
  }
  if (data.auth_method === METHOD_OAUTH) return `Client secret rotated${byText(data)}${grace}`
  return `Token rotated${byText(data)}${grace}`
}

// OAuth access tokens, one counted entry: "Got 24 access tokens in the
// last day (40 in all)".
const tokensIssuedText = data => {
  const day = Number(data && data.last_day) || 0
  const total = Number(data && data.total) || 0
  if (day > 0) return `Got ${plural(day, 'access token')} in the last day${total > day ? ` (${total} in all)` : ''}`
  return `Got ${plural(total, 'access token')} in all; none in the last day`
}

// A group event's group: its name as it is now (content adds it), else
// nothing.
const groupText = data => (data && data.group_name ? ` ${data.group_name}` : '')

// A membership change's counts: "(3 added, 1 removed)", leaving out a zero.
const countsText = data => {
  const parts = []
  const added = Number(data && data.added)
  const removed = Number(data && data.removed)
  if (Number.isFinite(added) && added > 0) parts.push(`${added} added`)
  if (Number.isFinite(removed) && removed > 0) parts.push(`${removed} removed`)
  return parts.length ? ` (${parts.join(', ')})` : ''
}

const membersText = data => {
  const n = Number(data && data.members)
  return Number.isFinite(n) && n > 0 ? ` with ${plural(n, 'member')}` : ''
}

// A refused write (scim.user.refused, content's activity from the request
// log): what the identity provider asked for, and why EducationXR refused
// it. Only the refused address's domain is ever known here, never the
// address.
const ADDRESS_REFUSALS = ['outside_domains', 'address_taken', 'unprovisioned']

const REFUSAL_REASONS = {
  outside_domains: domain => (domain ? `a domain this connection can't provision` : "that address's domain isn't one this connection can provision"),
  address_taken: () => 'another account already has that address',
  unprovisioned: () => "that address can't be provisioned",
  staff: () => "EducationXR staff accounts aren't managed by provisioning",
  last_owner: () => "that person is the organization's last owner",
  user_name_taken: () => 'another person on this connection has that user name',
  external_id_taken: () => 'another person on this connection has that external ID',
  already_here: () => 'that person is already provisioned on this connection under another user name',
  plan_required: () => "adding or reactivating people needs the organization's SSO plan",
  no_seats: () => 'no seats are left in this organization'
}

const withoutFinalStop = text => String(text || '').replace(/\.\s*$/, '')

export const refusedText = data => {
  const domain = data && typeof data.domain === 'string' ? data.domain : ''
  const reason = data && data.reason
  const operation = data && data.operation
  const addressRefusal = ADDRESS_REFUSALS.includes(reason)
  const known = REFUSAL_REASONS[reason]
  let why = known ? known(domain) : withoutFinalStop(data && data.detail)
  if (known && domain && reason === 'outside_domains') {
    why = operation === 'create' ? `it's ${why}` : `the new address is at ${domain}, ${why}`
  } else if (known && domain && addressRefusal && operation !== 'create') {
    why = `${why} (the new address is at ${domain})`
  }
  let what
  if (operation === 'create') what = domain && addressRefusal ? `Refused to add a person at ${domain}` : 'Refused to add a person'
  else if (operation === 'delete') what = 'Refused to remove a person'
  else what = addressRefusal ? 'Refused an email change' : 'Refused a change to a person'
  return why ? `${what}: ${why}` : what
}

export const eventText = event => {
  const data = (event && event.data) || {}
  switch (event && event.action) {
    case 'scim.group.created': return `Created group${groupText(data)}${membersText(data)}`
    case 'scim.group.updated': return `Updated group${groupText(data)}${fieldsText(data)}`
    case 'scim.group.members_updated': return `Updated group members${groupText(data) ? ` of${groupText(data)}` : ''}${countsText(data)}`
    case 'scim.group.deleted': return `Deleted group${groupText(data)}`
    case 'scim.user.created': return data.adopted ? 'Took over an existing member' : 'Added a person'
    case 'scim.user.updated': return `Updated a person${fieldsText(data)}`
    case 'scim.user.email_changed': return "Changed a person's email address"
    case 'scim.user.deactivated': return `Deactivated a person${sessionsText(data)}`
    case 'scim.user.deleted': return `Removed a person${sessionsText(data)}`
    case 'scim.user.activated': return 'Reactivated a person'
    case 'scim.user.refused': return refusedText(data)
    case 'api_key.created': return `Provisioning turned on${byText(data)}${data.prefix ? ` (token ${data.prefix}…)` : ''}`
    case 'api_key.rotated': return rotatedText(data)
    case 'scim.oauth.tokens_issued': return tokensIssuedText(data)
    case 'api_key.revoked': return `Token revoked${byText(data)}${data.reason ? `: ${data.reason}` : ''}`
    case 'api_key.updated': return `Token changed${byText(data)}`
    default: return event && event.action ? String(event.action).replace(/[._]/g, ' ') : 'Something happened'
  }
}

// Whether an event was the identity provider's (scim.*) or a person's
// (the token's api_key.*).
// Whether an event is a refusal, shown with a warning look.
export const eventRefused = event => Boolean(event && event.action === 'scim.user.refused')

export const eventByIdentityProvider = event => Boolean(event && typeof event.action === 'string' && event.action.startsWith('scim.'))

// The person an event is about (its users row), for a link to them.
export const eventUserID = event => {
  const id = event && event.data && Number(event.data.user_id)
  return Number.isFinite(id) && id > 0 ? id : null
}

// A failed request: "PATCH /Users/:id".
// The person a failed request named (a member of this organization), for a link.
export const failureUserID = failure => {
  const id = failure && Number(failure.userId)
  return Number.isFinite(id) && id > 0 ? id : null
}

export const failureRequestText = failure => [failure && failure.method, failure && failure.route].filter(Boolean).join(' ')
